import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";
import { cn } from "../../lib/utils";

// Sprint 39 — Professional SaaS color injection.
// Soft corners (--radius 0.5rem), teal-tinted shadow on primary, sentence-case
// labels. Secondary keeps a 2px outline but now in teal to align with the brand.
// Variant scheme:
//   primary     — solid teal fill + soft shadow, white text
//   secondary   — 1px teal outline, transparent fill
//   destructive — solid red fill + soft shadow
//   ghost       — text-only, hover overlay
//   link        — underline-on-hover
//   outline     — alias of secondary, retained for backward compat
//   brand       — alias of primary, retained for backward compat
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold tracking-normal transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 font-public-sans",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-teal hover:bg-primary/90 active:bg-primary/80",
        primary:
          "bg-primary text-primary-foreground shadow-teal hover:bg-primary/90 active:bg-primary/80",
        destructive:
          "bg-destructive text-destructive-foreground shadow-teal hover:bg-destructive/90",
        secondary:
          "bg-transparent border border-primary text-primary hover:bg-primary/10 active:bg-primary/15",
        outline:
          "bg-transparent border border-primary text-primary hover:bg-primary/10 active:bg-primary/15",
        ghost:
          "bg-transparent text-foreground hover:bg-primary/10 hover:text-primary",
        link:
          "text-accent underline-offset-4 hover:underline tracking-normal normal-case",
        brand:
          "bg-primary text-primary-foreground shadow-teal hover:bg-primary/90",
        // #7 — amber "organize" accent used by the global Add New Task buttons.
        organize:
          "bg-organize text-organize-foreground shadow-teal hover:bg-organize/90 active:bg-organize/80",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-8 px-3 text-xs",
        lg: "h-12 px-7 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
